import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Code, Palette, BookOpen, Award, Briefcase, Star, Layers } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const CATEGORIES = [
  { id: "project", label: "Project", icon: Code, color: "bg-blue-100 text-blue-700" },
  { id: "art", label: "Art & Design", icon: Palette, color: "bg-pink-100 text-pink-700" },
  { id: "writing", label: "Writing", icon: BookOpen, color: "bg-green-100 text-green-700" },
  { id: "award", label: "Award", icon: Award, color: "bg-yellow-100 text-yellow-700" },
  { id: "leadership", label: "Leadership", icon: Briefcase, color: "bg-purple-100 text-purple-700" },
];

const PortfolioPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("project");
  const [skills, setSkills] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && token) fetchItems();
  }, [user, token]);

  const fetchItems = async () => {
    try {
      const data = await apiCall("GET", "/portfolio", null, token);
      setItems(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSubmitting(true);
    try {
      await apiCall("POST", "/portfolio", {
        title, description, category,
        skills: skills.split(",").map(s => s.trim()).filter(Boolean),
      }, token);
      toast.success("Added to portfolio!");
      setTitle(""); setDescription(""); setSkills(""); setShowForm(false);
      fetchItems();
    } catch (err) { toast.error("Failed to add"); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    try {
      await apiCall("DELETE", `/portfolio/${id}`, null, token);
      toast.success("Removed"); fetchItems();
    } catch (err) { toast.error("Failed"); }
  };

  if (!user) { navigate("/portal"); return null; }

  const catInfo = (id) => CATEGORIES.find(c => c.id === id) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="portfolio-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers size={28} />
              <div>
                <h1 className="text-2xl font-bold">My Portfolio</h1>
                <p className="text-white/70 text-sm">Showcase your best work and achievements</p>
              </div>
            </div>
            <button onClick={() => setShowForm(!showForm)} className={`px-5 py-2.5 bg-[#FF6B00] text-white font-semibold rounded-xl flex items-center gap-2 ${btn3D}`} data-testid="add-item-btn">
              <Plus size={16} /> Add Work
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {showForm && (
          <Card className="shadow-lg border-2 border-[#FF6B00]/20" data-testid="portfolio-form">
            <CardContent className="p-5 space-y-3">
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Project title" className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-400" data-testid="title-input" />
              <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what you did and what you learned..." rows={3} className="w-full px-4 py-3 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-400" data-testid="desc-input" />
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map(c => (
                  <button key={c.id} onClick={() => setCategory(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${category === c.id ? "bg-[#002147] text-white" : "bg-gray-100 text-gray-600"}`}>{c.label}</button>
                ))}
              </div>
              <input type="text" value={skills} onChange={e => setSkills(e.target.value)} placeholder="Skills used (comma separated: Python, HTML, Teamwork)" className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-400" data-testid="skills-input" />
              <button onClick={handleSubmit} disabled={submitting} className={`w-full py-3 bg-[#002147] text-white font-bold rounded-xl ${btn3D} disabled:opacity-50`} data-testid="save-btn">{submitting ? "Saving..." : "Save to Portfolio"}</button>
            </CardContent>
          </Card>
        )}

        {items.length === 0 && !showForm && (
          <div className="text-center py-16">
            <Layers size={48} className="mx-auto text-[#002147]/20 mb-3" />
            <p className="text-[#002147]/50 font-medium">Your portfolio is empty</p>
            <p className="text-[#002147]/30 text-sm mt-1">Click "Add Work" to start showcasing your best projects</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((item) => {
            const cat = catInfo(item.category);
            const CatIcon = cat.icon;
            return (
              <Card key={item.id} className="hover:shadow-lg transition-shadow" data-testid={`portfolio-item-${item.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className={`w-10 h-10 rounded-xl ${cat.color} flex items-center justify-center`}><CatIcon size={18} /></div>
                    <button onClick={() => handleDelete(item.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                  <h3 className="font-bold text-[#002147] mt-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{item.description}</p>
                  {item.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {item.skills.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-3">{new Date(item.created_at).toLocaleDateString()}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PortfolioPage;
